/**
 * エリアグループラベルをマップに組み込む
 *
 * 設定の解決・ラベルの作成・絞り込みとズームの変更の受け口・ラベルクリック時の地図操作・最寄りのラベルへの案内を持つ
 * 店舗データは自分では取得せず、PointServiceが持つものを読む
 * エリアへの割り当てとラベルのDOMは points/arealabels/ 配下に委ねる
 */

import MapService from '@/services/MapService.ts'
import PointService from '@/services/PointService.ts'
import { GuideToPointManager } from '@/services/Guide.js'
import { AreaLabelManager } from '@/points/arealabels/AreaLabelManager.ts'
import { isTargetableReservedField } from '@/points/arealabels/areaMatching.ts'
import type { AreaMembership } from '@/points/arealabels/AreaMembership.ts'

/**
 * 件数が0のラベルを隠すかの既定
 *
 * 店舗が無いエリアのラベルを残すと、絞り込みの結果が読み取りにくい
 */
const DEFAULT_HIDE_EMPTY = true

/**
 * 初期ズームの指定が無い組織でラベルから寄せるときの最大ズーム
 *
 * 地図の生成が初期ズームに使う既定と同じ値にする
 */
const DEFAULT_FIT_MAX_ZOOM = 16

/**
 * 最寄りのラベルへの案内に渡す候補
 */
type GuideTarget = {
  latitude: number
  longitude: number

  /**
   * 地図の中心からの距離（メートル）
   *
   * 案内がこの値で並べ替えて最寄りを決める
   */
  distance: number

  /**
   * 案内を押したときの計測イベントに、どのエリアへ移動したかを入れるのに使う
   */
  area: AreaLabelArea
}

export default {
  /**
   * ラベル群のライフサイクル管理
   *
   * 全店舗が揃うまでは作れないので、init を呼ぶまで null
   */
  manager: null as AreaLabelManager | null,

  /**
   * 最寄りのラベルへの案内
   *
   * 店舗への案内と同じ見た目で、最寄りのラベルの方角を示す
   *
   * 表示範囲が決まってから作るので、それまでは null
   */
  guide: null as GuideToPointManager | null,

  /**
   * ラベルに使うエリア
   *
   * 都道府県も組織ごとのエリアもバックエンドが定義を持つので、フロントエンドでは足さない
   * 先にあるものから順に判定するので、バックエンドから受け取った並びのまま使う
   */
  get areas(): AreaLabelArea[] {
    return storelocator.map?.area_labels?.areas ?? []
  },

  /**
   * 機能が有効か
   */
  get isEnabled(): boolean {
    return storelocator.map?.area_labels?.enabled === true
  },

  /**
   * ラベルを出せるか
   *
   * 機能が有効で、かつ出せなくなっていないかを答える
   * ラベルを出すズーム域に居るかは含まない
   *
   * まだ作れていないだけなら true を返す
   *
   * ズーム誘導のメッセージを出すかの判断にも使うので、作られない状態では false へ倒す
   * true のまま作られないと、ピンもラベルも誘導も出ない画面になる
   */
  get canShow(): boolean {
    if (!this.isEnabled) {
      return false
    }

    // 全店舗を1回で取得するモードでなければラベルは作らない
    // 店舗ページと、preload_all を立て忘れたマップページが該当する
    if (!PointService.isPreloadAllMode) {
      return false
    }

    // 割り当て先が無ければラベルは作られない
    // 設定と応答のずれなので、先読みの決着を待っても受け取れない
    if (!this.areas.length) {
      return false
    }

    // 先読みを諦めると全店舗が揃わないので、以降ラベルは作られない
    return !PointService.isPreloadAllAborted
  },

  /**
   * そのズームでラベルを出すか
   */
  isLabelZoom(zoom: number): boolean {
    if (!this.canShow) {
      return false
    }

    // 個別のピンを隠すズームに合わせる
    // ピンが出ないズーム域とラベルが出るズーム域が一致するので、どちらも出ない帯ができない
    return zoom < MapService.minZoom
  },

  /**
   * ラベルを作る
   *
   * マップの初期化から1回だけ呼ぶ
   * 全店舗が揃ってからでないと件数が数えられないので、先読みの決着より後に呼ぶ
   *
   * 作れない設定・状態のときはここで警告を出す
   * 呼び出しがマップページに限られるので、店舗ページの絞り込みでは警告が出ない
   */
  init() {
    if (!this.isEnabled) {
      return
    }

    if (!this._ensureManager()) {
      return
    }

    this._applyCurrentState()
  },

  /**
   * 絞り込みの適用後に件数を数え直す
   *
   * ラベルを作る前の適用では何もしない、作るのは init が行う
   * 店舗の取得経路には関知しない
   */
  onFilterApplied() {
    if (!this.isEnabled || !this.manager) {
      return
    }

    this._applyCurrentState()
  },

  /**
   * 地図の移動後に表示を切り替える
   *
   * 移動のたびに呼ばれるので、件数は数え直さない
   * ラベルをまだ作っていなければ何もしない、作るのは init が行う
   */
  onMapUpdated(zoom: number) {
    if (!this.isEnabled || !this.manager) {
      return
    }

    this.manager.setVisible(this.isLabelZoom(zoom))
    this._updateGuide()
  },

  /**
   * エリアの判定に使うフィールドのキー
   *
   * 既定の解決はバックエンドが持つので、enabled が true なら必ず入る
   */
  get _targetField(): string {
    return storelocator.map?.area_labels?.target_field ?? ''
  },

  /**
   * ラベルから寄せるときの最大ズーム
   *
   * 地図の初期ズームに合わせる
   * 店舗が1件だけのエリアは範囲が点になり、上限が無いと県のどこを見ているか分からないほど寄る
   * 地図を開いたときと同じ寄り具合なら、周りとの位置関係も読める
   */
  get _fitMaxZoom(): number {
    return storelocator.map?.location?.default?.zoom ?? DEFAULT_FIT_MAX_ZOOM
  },

  /**
   * ラベルをまだ作っていなければ作る
   *
   * init を2回呼んでも作り直さないよう、作ってあるかを見てから作る
   * 全店舗は PointService が取得したものを読み、自分では取得しない
   *
   * @returns ラベルを使える状態か
   */
  _ensureManager(): boolean {
    if (this.manager) {
      return true
    }

    if (!PointService.isPreloadAllMode) {
      console.warn(
        '[AreaLabels] area_labels.enabled が true ですが、points.preload_all が true ではありません。'
        + '読み込み済みの店舗だけで数えると実際と違う件数を見せてしまうので、ラベルを作りません。'
      )
      return false
    }

    // 先読みが失敗するとジオハッシュ単位の取得に切り替わり、全店舗が揃わない
    if (!PointService.isPreloadAllSucceeded) {
      console.warn(
        '[AreaLabels] 全店舗の先読みが済んでいないので、ラベルを作りません。'
        + '先読みが失敗したままなら、以降もラベルは出ません。'
      )
      return false
    }

    const points = PointService.points

    if (!points.length) {
      console.warn(
        '[AreaLabels] 店舗が1件も無いので、ラベルを作りません。'
        + 'extra_config の points.default が未設定だと、ポイント一覧APIが0件を返します。'
      )
      return false
    }

    // 有効な組織にはバックエンドが定義を一緒に返すので、空なら設定と応答がずれている
    if (!this.areas.length) {
      console.warn(
        '[AreaLabels] area_labels.enabled が true ですが、エリアの定義を受け取れていません。'
        + '割り当て先が無いのでラベルを作りません。'
      )
      return false
    }

    this.manager = new AreaLabelManager(MapService.map, {
      hideEmpty: storelocator.map?.area_labels?.hide_empty ?? DEFAULT_HIDE_EMPTY,
      onLabelClicked: membership => this._onLabelClicked(membership)
    })

    this._warnUnusableTargetField()
    this.manager.setPoints(points, this.areas, this._targetField)

    return true
  },

  /**
   * 判定に使えないフィールドを指定していたら警告する
   *
   * 値を引けるのは判定に使える予約フィールドと追加フィールドだけで、それ以外はどのエリアにも一致しない
   * ラベルを作るのは1回だけなので、店舗ごとの判定では警告しない
   */
  _warnUnusableTargetField() {
    // 予約フィールドはフィールド設定に無くても値を引ける
    if (isTargetableReservedField(this._targetField)) {
      return
    }

    // 追加フィールドはフィールド設定の is_extra で値の在り処が決まる
    if (storelocator.fields?.[this._targetField]?.is_extra === true) {
      return
    }

    console.warn(
      `[AreaLabels] 判定に使えないフィールドを指定しています target_field=${this._targetField}`
      + ' 判定に使えるのは追加フィールドと、予約フィールドのうち key・name・address の3つだけです。'
      + '追加フィールドのつもりならフィールド設定に登録されているか確かめてください。'
    )
  },

  /**
   * 今の絞り込みとズームをラベルに反映する
   *
   * 件数は PointService.filterdPoints から数える
   * PointManager.filtered_points はズームの下限を下回ると空になるので、ラベルを出すズーム域では使えない
   */
  _applyCurrentState() {
    this.manager.updateCounts(PointService.filterdPoints)
    this.manager.setVisible(this.isLabelZoom(MapService.map.getZoom()))
    this._updateGuide()
  },

  /**
   * 表示範囲にラベルが1枚も無ければ、最寄りのラベルへの案内を表示する
   *
   * 表示範囲の外のラベルの中から、地図の中心に最も近いものを案内する
   */
  _updateGuide() {
    // 表示範囲は地図の初回描画で決まるので、それより前の init では案内を表示しない
    // 描画後の地図の移動で onMapUpdated からここを通るので、案内はそのときに表示する
    const bounds = MapService.map.getBounds()

    if (!bounds) {
      return
    }

    if (!this.guide) {
      this.guide = this._createGuide()
    }

    this.guide.update(this._guideTargets(bounds))
  },

  /**
   * 案内する先の候補
   *
   * 表示範囲にラベルが1枚でもあれば空を返し、案内を消させる
   * 案内が並べ替えに使うので、地図の中心からの距離を持たせる
   *
   * @param bounds 地図の表示範囲
   */
  _guideTargets(bounds: google.maps.LatLngBounds): GuideTarget[] {
    const areas = this.manager.getShownAreas()
    const toLatLng = (area: AreaLabelArea) => new google.maps.LatLng(area.location.latitude, area.location.longitude)

    if (areas.some(area => bounds.contains(toLatLng(area)))) {
      return []
    }

    const center = MapService.map.getCenter()

    return areas.map(area => ({
      latitude: area.location.latitude,
      longitude: area.location.longitude,
      distance: google.maps.geometry.spherical.computeDistanceBetween(center, toLatLng(area)),
      area: area
    }))
  },

  /**
   * 最寄りのラベルへの案内を作る
   *
   * 案内を押すとラベルの位置へ地図を動かすので、ラベルが表示範囲に入り、次の地図の移動で案内が消える
   */
  _createGuide(): GuideToPointManager {
    return new GuideToPointManager(MapService.map, {
      messages: {
        move_to_point: storelocator.messages.common_move_to_nearest_point
      },
      // ラベルの位置はエリアごとに決めた固定の座標で店舗の位置ではないので、距離を表示すると店舗までの距離と読み違える
      distance: { show: false },
      onMoveClicked: (target: GuideTarget) => this._sendGuideClickedEvent(target)
    })
  },

  /**
   * ラベルがクリックされたとき、計測イベントを送り、そのエリアの店舗が収まる範囲に合わせて地図を表示する
   *
   * ラベルに出した件数を数えたときの結果をそのまま使う
   * 絞り直すと、ラベルの件数と寄せた先に見える店舗がずれ得る
   *
   * 寄せ具合は店舗の散らばりに合わせる
   * 一定のズームにすると、店舗が1件のエリアでは引きすぎ、広いエリアでは収まらない
   */
  _onLabelClicked(membership: AreaMembership) {
    this._sendLabelClickedEvent(membership)

    if (membership.isEmpty) {
      return
    }

    const bounds = new google.maps.LatLngBounds()

    membership.getShownPoints().forEach(point => {
      bounds.extend(new google.maps.LatLng(point.latitude, point.longitude))
    })

    // 絞り込みを通った店舗が収まる範囲へ寄せる
    MapService.fitBounds(bounds, this._fitMaxZoom)

    // 店舗が広く散らばるエリアでは、範囲に合わせても個別のピンが表示されるズームまで拡大されない
    // ラベルを押しても何も起きないように見えるので、ピンが出るところまで入れる
    if (this.isLabelZoom(MapService.map.getZoom())) {
      MapService.map.setZoom(MapService.minZoom)
    }
  },

  /**
   * ラベルの押下をGAへ送る
   *
   * 選択中のマーカー・検索条件はどのイベントにも付くcontextに入るので、ここでは送らない
   *
   * 押した時点でどこまで引いて見ていたかを知りたいので、ズームは地図を寄せる前に読む
   * 押しても地図が動かない操作がどれだけあるかを数えられるよう、件数が0のラベルでも送る
   */
  _sendLabelClickedEvent(membership: AreaMembership) {
    storelocator.analytics.client?.send('SelectAreaLabel', {
      AreaId: membership.area.area_code,
      AreaName: membership.area.label,
      AreaPointCount: membership.shownCount,
      ZoomLevel: MapService.map.getZoom(),
    })
  },
  /**
   * 最寄りのラベルへの案内の押下をGAへ送る
   *
   * 店舗への案内の MoveToNearestPoint とは別のイベントにする
   * 同じイベントにすると、ピンを表示するズーム域での店舗への移動と、引いた地図でのラベルへの移動を区別できない
   *
   * 案内は地図を平行移動するだけでズームを変えないので、押した時点のズームがそのまま移動後のズームになる
   */
  _sendGuideClickedEvent(target: GuideTarget) {
    storelocator.analytics.client?.send('MoveToNearestAreaLabel', {
      AreaId: target.area.area_code,
      AreaName: target.area.label,
      ZoomLevel: MapService.map.getZoom(),
    })
  },
}
